import { z } from "zod";

import http from "@/services/http";

const classRegisterRequest = z.object({
  instanceId: z.string(),
  parentOrderId: z.string(),
});

export type ClassRegisterRequest = z.infer<typeof classRegisterRequest>;

// GET /upcoming-user-classes/:courseId (apollo-middleware-service) forwards the raw response
// body from an upstream course-instance service byte-for-byte (no internal DTO), so this shape
// is modeled from the real consumer (UpcomingClasses.vue) rather than apollo-middleware's own
// (differently-shaped) dto.Instance.
const upcomingClass = z
  .object({
    instanceId: z.string(),
    classStartDate: z.string().nullish(),
    classEndDate: z.string().nullish(),
    numAvailableSeats: z.number().nullish(),
    enrollmentStatusId: z.string().nullish(),
    isEnrolled: z.string().nullish(),
  })
  .loose();

export const upcomingClassesResponse = z
  .object({
    data: z.array(upcomingClass),
  })
  .loose();

export async function fetchUpcomingClasses(classID: number) {
  const { data } = await http().get(
    globalThis.env.API_APOLLO_MIDDLEWARE + "/upcoming-user-classes/" + classID
  );
  return upcomingClassesResponse.parse(data);
}

// POST /register-learner - responds {message: string} on success (dto.Response).
export const registerResponse = z.object({ message: z.string() }).loose();

export async function registerClasses(request: ClassRegisterRequest) {
  const { data } = await http().post(
    globalThis.env.API_APOLLO_MIDDLEWARE + "/register-learner",
    classRegisterRequest.parse(request)
  );
  return registerResponse.parse(data);
}

// GET /unregister-user/:instanceId also forwards a raw upstream byte response - the consumer
// only checks truthiness, so this is intentionally left unvalidated/untyped like the original.
export async function unRegisterClasses(instanceId: string) {
  const { data } = await http().get(
    globalThis.env.API_APOLLO_MIDDLEWARE + "/unregister-user/" + instanceId
  );
  return data;
}

// GET /instances?days=30 (apollo-middleware-service) - []dto.Instance, returned as a bare array.
const instance = z
  .object({
    statusId: z.string().nullish(),
    course: z
      .object({
        acronym: z.string().nullish(),
        title: z.string().nullish(),
        courseSourceIdentifier: z.string().nullish(),
        productId: z.string().nullish(),
        technology: z.string().nullish(),
        version: z.string().nullish(),
      })
      .loose()
      .nullish(),
    startDate: z.string().nullish(),
    endDate: z.string().nullish(),
    startTime: z.string().nullish(),
    endTime: z.string().nullish(),
    productId: z.string().nullish(),
  })
  .loose();

export const instancesResponse = z.array(instance);

export async function getUpcomingClasses() {
  const { data } = await http().get(globalThis.env.API_APOLLO_MIDDLEWARE + "/instances?days=30");
  return instancesResponse.parse(data);
}
